import { useEffect, useRef, useState } from 'react';
import type { Variante } from '../types';

type Props = {
  variante: Variante;
  audioUrl: string | null;
  fotoUrl: string | null;
  nomeHomenageado: string;
  onPlayingChange?: (playing: boolean) => void;
};

function fmtTime(s: number) {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function MusicPlayer({ variante, audioUrl, fotoUrl, nomeHomenageado, onPlayingChange }: Props) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [imgBroken, setImgBroken] = useState(false);

  // Reset quando troca variante: pausa, recomeça do início e recarrega
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    onPlayingChange?.(false);
    audio.load();
  }, [audioUrl, onPlayingChange]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => { setPlaying(false); onPlayingChange?.(false); };
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, [onPlayingChange]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      onPlayingChange?.(false);
    } else {
      audio.play().then(() => {
        setPlaying(true);
        onPlayingChange?.(true);
      }).catch(() => {});
    }
  };

  const seekByClientX = (clientX: number) => {
    const audio = audioRef.current;
    const bar = progressRef.current;
    if (!audio || !bar || !audio.duration) return;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audio.currentTime = pct * audio.duration;
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    seekByClientX(e.clientX);
    const onMove = (ev: MouseEvent) => seekByClientX(ev.clientX);
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleProgressTouch = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!e.touches[0]) return;
    seekByClientX(e.touches[0].clientX);
  };

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const showFoto = !!fotoUrl && !imgBroken;

  return (
    <div className="mz-player">
      {/* Outer ring com sombra dramática + grain texture */}
      <div className="mz-vinyl-outer">
        {/* Bordas concêntricas — grooves do vinil */}
        <div className={`mz-vinyl-disc ${playing ? 'mz-spin' : ''}`}>
          <div className="mz-grooves" aria-hidden />

          {/* Label central com a foto */}
          <div className="mz-vinyl-label">
            {showFoto ? (
              <img
                src={fotoUrl as string}
                alt={`Foto de ${nomeHomenageado}`}
                onError={() => setImgBroken(true)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '50%',
                }}
              />
            ) : (
              <div className="mz-label-fallback">
                <div className="mz-monogram">{(nomeHomenageado || '?').slice(0, 1).toUpperCase()}</div>
                <div className="mz-monogram-sub">INSPIRA</div>
              </div>
            )}
            {/* Furinho central */}
            <div className="mz-spindle" aria-hidden />
          </div>

          {/* Brilho/reflexão sutil */}
          <div className="mz-shine" aria-hidden />
        </div>

        {/* Botão play sobre tudo */}
        <button
          type="button"
          onClick={togglePlay}
          className={`mz-play-btn ${playing ? 'mz-play-active' : ''}`}
          aria-label={playing ? 'Pausar música' : 'Tocar música'}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
              <rect x="6" y="5" width="4" height="14" rx="1" />
              <rect x="14" y="5" width="4" height="14" rx="1" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="34" height="34" fill="currentColor">
              <path d="M8 5.14v13.72c0 .79.87 1.27 1.54.84l10.4-6.86a1 1 0 0 0 0-1.68L9.54 4.3C8.87 3.87 8 4.35 8 5.14z" />
            </svg>
          )}
        </button>
      </div>

      {/* Etiqueta de versão + nome */}
      <div className="mz-meta">
        <div className="mz-version-tag">
          <span className="mz-stamp-dot" aria-hidden />
          Versão {variante === 'A' ? 'I' : 'II'} · da nossa composição pra
        </div>
        <h2 className="mz-name">{nomeHomenageado || 'sua pessoa'}</h2>
      </div>

      {/* Barra de progresso */}
      <div className="mz-progress-wrap">
        <div
          ref={progressRef}
          className="mz-progress"
          onMouseDown={handleProgressMouseDown}
          onTouchStart={handleProgressTouch}
          onTouchMove={handleProgressTouch}
          role="slider"
          aria-label="Progresso da música"
          aria-valuemin={0}
          aria-valuemax={Math.floor(duration)}
          aria-valuenow={Math.floor(currentTime)}
          tabIndex={0}
        >
          <div className="mz-progress-fill" style={{ width: `${pct}%` }} />
          <div className="mz-progress-knob" style={{ left: `${pct}%` }} />
        </div>
        <div className="mz-times">
          <span>{fmtTime(currentTime)}</span>
          <span className="mz-times-sep">·</span>
          <span>{fmtTime(duration || 0)}</span>
        </div>
      </div>

      <audio ref={audioRef} src={audioUrl || undefined} preload="metadata" />

      <style>{`
        .mz-player {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 18px 16px 22px;
        }

        .mz-vinyl-outer {
          position: relative;
          width: min(82vw, 320px);
          aspect-ratio: 1;
          border-radius: 50%;
          background:
            radial-gradient(circle at 50% 50%, #181520 0%, #0c0910 70%, #050308 100%),
            radial-gradient(circle at 30% 28%, rgba(255,255,255,0.06) 0%, transparent 35%);
          box-shadow:
            0 22px 60px -12px rgba(24, 12, 32, 0.6),
            0 8px 22px -4px rgba(0,0,0,0.45),
            inset 0 0 0 1px rgba(255,176,122,0.08),
            inset 0 -8px 16px rgba(0,0,0,0.6);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mz-vinyl-disc {
          position: relative;
          width: 100%;
          height: 100%;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.4s ease;
        }

        @keyframes mz-spin-anim {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        .mz-spin {
          animation: mz-spin-anim 5.5s linear infinite;
        }

        .mz-grooves {
          position: absolute;
          inset: 6%;
          border-radius: 50%;
          background:
            repeating-radial-gradient(
              circle,
              rgba(255,255,255,0.025) 0px,
              rgba(255,255,255,0.025) 1px,
              transparent 1px,
              transparent 4px
            );
          pointer-events: none;
        }

        .mz-shine {
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background:
            radial-gradient(ellipse 65% 38% at 32% 24%, rgba(255,255,255,0.12) 0%, transparent 55%),
            radial-gradient(ellipse 36% 20% at 70% 76%, rgba(255,180,120,0.08) 0%, transparent 60%);
          pointer-events: none;
        }

        .mz-vinyl-label {
          position: relative;
          width: 48%;
          aspect-ratio: 1;
          border-radius: 50%;
          overflow: hidden;
          background: var(--gold-pale);
          box-shadow:
            inset 0 0 0 6px rgba(255,255,255,0.92),
            inset 0 0 0 7px rgba(255,145,77,0.45),
            0 4px 14px rgba(0,0,0,0.35);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mz-label-fallback {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, var(--gold) 0%, var(--amber-deep) 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #fff;
        }

        .mz-monogram {
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-weight: 800;
          font-style: italic;
          font-size: clamp(40px, 8vw, 60px);
          line-height: 1;
          letter-spacing: -0.04em;
        }

        .mz-monogram-sub {
          font-family: 'DM Sans', sans-serif;
          font-size: 9px;
          font-weight: 700;
          letter-spacing: 0.32em;
          margin-top: 6px;
          opacity: 0.85;
        }

        .mz-spindle {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 8%;
          aspect-ratio: 1;
          background: #0a0703;
          border-radius: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 0 0 2px rgba(255,255,255,0.5) inset, 0 1px 2px rgba(0,0,0,0.6);
        }

        .mz-play-btn {
          position: absolute;
          width: 68px;
          height: 68px;
          border-radius: 50%;
          background: linear-gradient(135deg, var(--gold) 0%, var(--gold-hover) 100%);
          color: #fff;
          border: 3px solid #fff;
          box-shadow:
            0 10px 28px rgba(255,145,77,0.55),
            0 0 0 1px rgba(204,102,32,0.40),
            inset 0 -3px 8px rgba(0,0,0,0.18);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          transition: transform 0.18s ease, box-shadow 0.18s ease;
          z-index: 2;
        }
        .mz-play-btn:hover { transform: scale(1.05); }
        .mz-play-btn:active { transform: scale(0.96); }
        .mz-play-btn:focus-visible {
          outline: 3px solid var(--gold-pale);
          outline-offset: 4px;
        }

        .mz-play-active svg rect:first-child { animation: mz-wiggle-l 0.6s ease-in-out infinite alternate; }
        .mz-play-active svg rect:last-child  { animation: mz-wiggle-r 0.6s ease-in-out infinite alternate 0.15s; }
        @keyframes mz-wiggle-l { from { transform: translateY(0); } to { transform: translateY(0.5px); } }
        @keyframes mz-wiggle-r { from { transform: translateY(0); } to { transform: translateY(-0.5px); } }

        .mz-meta {
          margin-top: 24px;
          text-align: center;
          max-width: 100%;
        }

        .mz-version-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-family: 'DM Sans', sans-serif;
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--text-muted);
          padding: 6px 14px 6px 10px;
          background: var(--gold-pale);
          border: 1px solid var(--gold-border);
          border-radius: 999px;
          margin-bottom: 12px;
        }

        .mz-stamp-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--gold);
          box-shadow: 0 0 0 3px rgba(255,145,77,0.18);
        }

        .mz-name {
          font-family: 'Playfair Display', 'Source Serif 4', serif;
          font-weight: 700;
          font-style: italic;
          font-size: clamp(34px, 8.5vw, 56px);
          line-height: 1;
          letter-spacing: -0.03em;
          color: var(--heading-ink);
          margin: 0;
          word-break: break-word;
          padding: 0 8px;
        }

        .mz-progress-wrap {
          width: 100%;
          max-width: 360px;
          margin-top: 22px;
          padding: 0 4px;
        }

        .mz-progress {
          position: relative;
          height: 6px;
          background: var(--border-soft);
          border-radius: 999px;
          cursor: pointer;
          touch-action: none;
        }

        .mz-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--gold) 0%, var(--amber-deep) 100%);
          border-radius: 999px;
          transition: width 0.08s linear;
        }

        .mz-progress-knob {
          position: absolute;
          top: 50%;
          width: 16px;
          height: 16px;
          background: #fff;
          border: 3px solid var(--gold);
          border-radius: 50%;
          transform: translate(-50%, -50%);
          box-shadow: 0 2px 6px rgba(255,145,77,0.45);
          pointer-events: none;
        }

        .mz-times {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 10px;
          font-family: 'DM Sans', sans-serif;
          font-size: 12.5px;
          font-weight: 600;
          color: var(--text-muted);
          font-variant-numeric: tabular-nums;
          letter-spacing: 0.05em;
        }

        .mz-times-sep {
          opacity: 0.5;
        }

        @media (min-width: 720px) {
          .mz-vinyl-outer { width: min(60vw, 380px); }
          .mz-play-btn { width: 78px; height: 78px; }
        }
      `}</style>
    </div>
  );
}
